import React from 'react'
import { LogEntry, ExceptionInfo } from '../types'
import type { LogPdfRow } from '../components/LogPdfDocument'

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return d.toISOString().replace('T', ' ').replace('Z', '')
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function escapeCsvField(value: string): string {
  // Neutralize CSV formula injection: a leading = + - @ (or tab/CR) is
  // interpreted as a formula by Excel/Sheets. Prefix with a single quote.
  let v = value
  if (/^[=+\-@\t\r]/.test(v)) {
    v = `'${v}`
  }
  if (v.includes(',') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`
  }
  return v
}

function getPayloadField(entry: LogEntry, key: string): string {
  const val = entry.metadata.payload?.[key]
  return typeof val === 'string' ? val : ''
}

function formatExceptions(entry: LogEntry): string {
  const raw = entry.metadata.payload?.exceptions
  if (!Array.isArray(raw) || raw.length === 0) return ''
  return (raw as ExceptionInfo[])
    .map(ex => `${ex.ClassName ?? 'Unknown'}: ${ex.Message ?? '(no message)'}`)
    .join('; ')
}

export function buildCsvContent(entries: LogEntry[]): string {
  const headers = [
    'Timestamp',
    'Level',
    'Type',
    'Subsystem',
    'MachineName',
    'ConnectionId',
    'User',
    'Description',
    'Message Template',
    'Exceptions',
    'Request ID',
    'Trace ID',
  ]
  const rows = entries.map(e => [
    formatTimestamp(e.timestamp),
    e.level,
    e.type,
    e.subsystem,
    getPayloadField(e, 'MachineName'),
    getPayloadField(e, 'ConnectionId'),
    e.user ?? '',
    e.description,
    getPayloadField(e, 'messageTemplate'),
    formatExceptions(e),
    e.metadata.requestId ?? '',
    e.metadata.traceId ?? '',
  ])

  return [
    headers.map(escapeCsvField).join(','),
    ...rows.map(row => row.map(escapeCsvField).join(',')),
  ].join('\n')
}

export function exportToCsv(
  entries: LogEntry[],
  filename = 'system-logs',
): void {
  const csvContent = buildCsvContent(entries)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `${filename}-${new Date().toISOString().slice(0, 10)}.csv`)
}

function buildPdfRows(entries: LogEntry[]): LogPdfRow[] {
  return entries.map(e => ({
    timestamp: formatTimestamp(e.timestamp),
    level: e.level,
    source: e.subsystem,
    machine: getPayloadField(e, 'MachineName'),
    connection: getPayloadField(e, 'ConnectionId'),
    message: e.description,
    exceptions: formatExceptions(e),
  }))
}

export async function exportToPdf(
  entries: LogEntry[],
  filename = 'system-logs',
): Promise<void> {
  // Loaded on demand so the heavy (ESM) @react-pdf/renderer only enters the
  // bundle when a PDF is actually exported, and never enters the Jest module
  // graph of this service's unit tests.
  const [{ pdf }, { default: LogPdfDocument }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('../components/LogPdfDocument'),
  ])
  const blob = await pdf(
    React.createElement(LogPdfDocument, {
      title: 'System Logs Export',
      generatedAt: new Date().toLocaleString(),
      rows: buildPdfRows(entries),
    }),
  ).toBlob()
  downloadBlob(blob, `${filename}-${new Date().toISOString().slice(0, 10)}.pdf`)
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
